import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useIdleTimer from '../hooks/useIdleTimer'

import { db } from '../firebase/config'
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore'

import Spinner from '../components/Spinner'
import { readPendingScores } from '../utils/offlineScores'

const mergePendingScores = (scoresList) => {
  const pending = readPendingScores()
  if (!pending.length) {
    return scoresList
  }

  const keyed = new Map(
    scoresList.map((entry) => {
      const key = entry.clientId || `${entry.firstName}-${entry.lastName}-${entry.score}`
      return [key, entry]
    })
  )

  pending.forEach((pendingEntry) => {
    const key = pendingEntry.clientId || `${pendingEntry.firstName}-${pendingEntry.lastName}-${pendingEntry.score}`
    if (!keyed.has(key)) {
      keyed.set(
        key,
        {
          ...pendingEntry,
          id: pendingEntry.clientId || pendingEntry.id || `pending-${pendingEntry.score}-${pendingEntry.createdAt}`,
          isPending: true,
        }
      )
    }
  })

  return Array.from(keyed.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 10)
}

const Leaderboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentClientId, setCurrentClientId] = useState(null)
  const [topScores, setTopScores] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isOffline, setIsOffline] = useState(false)

  useEffect(() => {
    // Retrieve scores from localStorage (persistent storage)
    const score = localStorage.getItem('lastGameScore')
    const storedClientId = localStorage.getItem('lastScoreClientId')
    
    if (storedClientId) {
      setCurrentClientId(storedClientId)
    }

    const parseCachedLeaderboard = () => {
      try {
        const stored = localStorage.getItem('cachedLeaderboard')
        return stored ? JSON.parse(stored) : []
      } catch (error) {
        console.error('Error parsing cached scores:', error)
        return []
      }
    }

    const addCurrentScoreToList = (scoresList) => {
      if (!score) return scoresList
      const scoreNum = parseInt(score, 10)
      const scoreExists = scoresList.some((s) => s.score === scoreNum && s.isCurrentSession)
      if (scoreExists) {
        return scoresList
      }

      const firstName = localStorage.getItem('firstName') || ''
      const lastName = localStorage.getItem('lastName') || ''
      const initials = localStorage.getItem('initials') || `${firstName}${lastName}`.trim()
      const clientId = localStorage.getItem('lastScoreClientId') || null

      const augmented = [
        ...scoresList,
        {
          id: `temp-${Date.now()}`,
          clientId,
          score: scoreNum,
          firstName,
          lastName,
          initials,
          timestamp: new Date().toISOString(),
          isCurrentSession: true,
        },
      ]

      return augmented.sort((a, b) => b.score - a.score).slice(0, 10)
    }

    const cachedScores = parseCachedLeaderboard()
    const offlineReadyScores = mergePendingScores(addCurrentScoreToList(cachedScores))

    setTopScores(offlineReadyScores)
    setIsLoading(false)

    if (!offlineReadyScores.length && typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsOffline(true)
    }

    localStorage.setItem('cachedLeaderboard', JSON.stringify(offlineReadyScores))

    // Fetch top scores from Firebase with real-time updates
    const scoresRef = collection(db, 'scores')
    const topScoresQuery = query(
      scoresRef,
      orderBy('score', 'desc'),
      limit(10) // Get top 10 scores
    )

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      topScoresQuery,
      (snapshot) => {
        const scores = []
        snapshot.forEach((doc) => {
          scores.push({
            id: doc.id,
            ...doc.data()
          })
        })
        
        // Update state with fresh data
        const mergedScores = mergePendingScores(scores)
        setTopScores(mergedScores)
        setIsLoading(false)
        setIsOffline(false)
        
        // Cache the scores in localStorage for offline use
        localStorage.setItem('cachedLeaderboard', JSON.stringify(scores))
        localStorage.setItem('leaderboardLastUpdated', new Date().toISOString())
      },
      (error) => {
        console.error('Error fetching scores:', error)
        setIsLoading(false)
        setIsOffline(true)
        
        // If we have cached scores, we're already showing them
        // If not, show empty state
        if (!cachedScores.length) {
          setTopScores([])
        }
      }
    )

    // Cleanup listener on unmount
    return () => unsubscribe()
  }, [])
  useIdleTimer()
  return (
    <div className="text-[60px] text-left w-[610px] h-[1060px] mx-auto mt-[210px]">
      <div className="flex flex-col w-[610px] mx-auto items-start justify-center">
        <h1 className="text-[66px] text-white uppercase font-euclid-circular-b-bold mt-[20px] mb-[20px]">
          {t('leaderboard')}
        </h1>
        
       

        {/* Leaderboard Section */}
        <div className="w-full  mx-auto h-[660px] overflow-y-auto pb-[140px]">
                  
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : topScores.length > 0 ? (
            <div className="">
              {topScores.map((scoreEntry, index) => {
                const fromField = (scoreEntry.initials || '').toString().trim()
                const firstInitial = scoreEntry.firstName ? scoreEntry.firstName.charAt(0).toUpperCase() : ''
                const lastInitial = scoreEntry.lastName ? scoreEntry.lastName.charAt(0).toUpperCase() : ''
                const computed = `${firstInitial}${lastInitial}`.trim()
                const initials = (fromField || computed).toUpperCase()

                const isCurrentUser =
                  (currentClientId && scoreEntry.clientId && scoreEntry.clientId === currentClientId) ||
                  scoreEntry.isCurrentSession === true
                
                // Check if next item is the user's score to skip border
                const nextIsCurrentUser =
                  index < topScores.length - 1 &&
                  ((currentClientId && topScores[index + 1].clientId === currentClientId) ||
                    topScores[index + 1].isCurrentSession === true)
                const showBorder = index < 9 && !nextIsCurrentUser
                
                return (
                  <div 
                    key={scoreEntry.id || index}
                    className={`flex items-center w-full h-[68px] pl-8 pr-14 leading-none ${
                      isCurrentUser ? ' bg-[#ffab54] rounded-xl border-[3px] border-white' : ''
                    } ${showBorder ? 'border-b-[1px] border-[#999db6]' : ''}`}
                  >
                    <span className={`text-[45px] font-euclid-circular-b-bold w-[60px] text-left ${
                        isCurrentUser ? 'text-[#001FCC]' : 'text-[#007dff]'
                      }`}>
                      {index + 1}
                    </span>

                    <div className="flex-1 flex items-center justify-center">
                      {initials && (
                        <span className={`text-[45px] text-center font-euclid-circular-b-bold ${
                          isCurrentUser ? 'text-[#001FCC]' : 'text-white'
                        }`}>
                          {initials}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-end w-[60px] gap-3">
                      <span className={`text-[45px] font-euclid-circular-b-bold ${
                        isCurrentUser ? 'text-[#001FCC]' : 'text-white'
                      }`}>
                        {scoreEntry.score}
                      </span>
                  {scoreEntry.isPending && (
                    <span className={`text-[24px] font-euclid-circular-b-regular italic ${
                      isCurrentUser ? 'text-[#001FCC]' : 'text-white'
                    }`}>
                      syncing…
                    </span>
                  )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              {isOffline ? (
                <div className="space-y-2">
                  <p className="text-gray-500">📡 No cached scores available</p>
                  <p className="text-sm text-gray-400">Connect to internet to see leaderboard</p>
                </div>
              ) : (
                <p className="text-gray-500">No scores yet. Be the first!</p>
              )}
            </div>
          )}

        </div>
          {/* Close Button */}
          <button 
            type="submit" 
            className="bg-[#ffab54] absolute bottom-[160px] left-[235px] w-[379px] h-[93px] flex items-center justify-center cursor-pointer rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              // Clear the last game score and flag, but keep the best score
              localStorage.removeItem('lastGameScore')
              localStorage.removeItem('isNewHighScore')
              localStorage.removeItem('lastScoreClientId')
              navigate('/')
            }}
          >
            <span className="relative z-10 text-[#000b48] text-[48px] font-euclid-circular-b-semibold">{t('end')}</span>
          </button>
      </div>
    </div>
  )
}

export default Leaderboard
